<template>
  <div class="relative min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">
    <div class="h-full container mx-auto">
      <div class="flex flex-col h-full w-full p-0 sm:p-8">

        <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
          <div class="flex items-center text-sm font-light">
            <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
            <div v-if="role != 'Admin Support'">
              <span class="mx-2">/</span>
              <router-link to="/partnerships" class="opacity-50 hover:opacity-100">Partnerships</router-link>
            </div>
            <span class="mx-2">/</span>
            <span>Security Clearance</span>
          </div>
          <div class="font-bold">Security Clearance</div>
        </div>

        <div class="mt-4 flex justify-end items-center gap-4">
          <div v-if="hasSuperPermission('security-clearance-initiate') && activeTab === 'initiated'"
            @click="openInitiateModal"
            class="w-40 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg cursor-pointer hover:text-ternary text-white">
            <icon-plus class="w-4 h-4"></icon-plus>
            <span class="text-xs ml-2">Initiate</span>
          </div>
          <div v-if="hasPermission('security-clearance-export-excel') && activeTab === 'list'">
            <div v-if="isExportExcel"
              class="w-40 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg hover:text-ternary text-white">
              <base-spinner-with-text class-list="w-4 h-4" text=""
                text-class-list="text-white"></base-spinner-with-text>
              <span class="text-xs">Exporting Excel</span>
            </div>
            <div v-else @click="generateReport"
              class="w-40 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg cursor-pointer hover:text-ternary text-white">
              <font-awesome-icon :icon="['fas', 'file-excel']" />
              <span class="text-xs ml-2">Export Excel</span>
            </div>
          </div>
        </div>

        <!-- Tabs Section -->
        <div class="mt-4">
          <!-- Tab Navigation -->
          <div
            v-if="hasSuperPermission('security-clearance-index') && hasSuperPermission('security-clearance-initiate')"
            class="bg-white rounded-2xl border shadow-lg mb-6">
            <div class="flex border-b">
              <button v-if="hasSuperPermission('security-clearance-index')" @click="activeTab = 'list'" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 rounded-l-2xl',
                activeTab === 'list'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'list']" class="mr-2" />
                  Security Clearance List
                </div>
              </button>
              <button v-if="hasSuperPermission('security-clearance-initiate')" @click="activeTab = 'initiated'" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 rounded-r-2xl',
                activeTab === 'initiated'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'file-alt']" class="mr-2" />
                  Initiated Requests
                </div>
              </button>
            </div>
          </div>

          <!-- Tab Content -->
          <section class="w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
            <SecurityClearanceList v-if="activeTab === 'list'" ref="securityClearanceList"
              @approve-or-reject="handleApproveOrReject" @generate-pdf="handleGeneratePdf"
              @delete-security-clearance="handleDeleteSecurityClearance" />
            <InitiatedRequests v-if="activeTab === 'initiated'" ref="initiatedRequests"
              @open-initiate-modal="openInitiateModal" @generate-pdf="handleGeneratePdf" />
          </section>
        </div>

        <transition name="fade" mode="out-in">
          <base-modal v-if="showSuccessModal" :title="`Security Clearance Initiated Successfully`"
            @close="closeSuccessModal">
            <div class="flex flex-col px-8 py-4">
              <p class="text-sm text-gray-700 mb-4">The security clearance has been initiated successfully. Share the
                link below:</p>
              <div class="flex items-center gap-2 mb-4">
                <input type="text" :value="initiationLink" readonly
                  class="flex-1 border rounded-lg py-2 px-3 text-sm bg-gray-50" />
                <button @click="copyLink" type="button"
                  class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-4 py-2 uppercase text-xs cursor-pointer">
                  <span v-if="isLinkCopied">Copied!</span>
                  <span v-else>Copy</span>
                </button>
              </div>
            </div>
          </base-modal>
        </transition>

        <transition name="fade" mode="out-in">
          <base-modal v-if="showInitiateModal" :title="`Initiate Security Clearance`" @close="closeInitiateModal">
            <form @submit.prevent="submitInitiateForm" class="flex flex-col px-8">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div class="flex flex-col" :class="{ invalid: !initiateForm.speaker.isValid }">
                  <label for="speaker">Speaker / Representative*</label>
                  <input type="text" id="speaker" v-model.trim="initiateForm.speaker.val" placeholder=""
                    @blur="clearInitiateValidity('speaker')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.speaker.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.speaker.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col" :class="{ invalid: !initiateForm.event.isValid }">
                  <label for="event">Event*</label>
                  <input type="text" id="event" v-model.trim="initiateForm.event.val" placeholder=""
                    @blur="clearInitiateValidity('event')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.event.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.event.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col sm:col-span-2" :class="{ invalid: !initiateForm.purpose_of_event.isValid }">
                  <label for="purpose_of_event">Purpose of the Event*</label>
                  <textarea id="purpose_of_event" v-model.trim="initiateForm.purpose_of_event.val" placeholder=""
                    @blur="clearInitiateValidity('purpose_of_event')"
                    class="bg-white resize-none w-full h-24 outline-none text-sm border p-4 rounded-lg"></textarea>
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.purpose_of_event.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.purpose_of_event.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col" :class="{ invalid: !initiateForm.target_group.isValid }">
                  <label for="target_group">Target Group*</label>
                  <input type="text" id="target_group" v-model.trim="initiateForm.target_group.val" placeholder=""
                    @blur="clearInitiateValidity('target_group')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.target_group.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.target_group.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col" :class="{ invalid: !initiateForm.age_group.isValid }">
                  <label for="age_group">Age Group*</label>
                  <input type="text" id="age_group" v-model.trim="initiateForm.age_group.val" placeholder=""
                    @blur="clearInitiateValidity('age_group')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.age_group.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.age_group.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col" :class="{ invalid: !initiateForm.date_time.isValid }">
                  <label for="date_time">Start Date*</label>
                  <input type="datetime-local" id="date_time" v-model.trim="initiateForm.date_time.val" placeholder=""
                    @blur="clearInitiateValidity('date_time')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.date_time.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.date_time.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col" :class="{ invalid: !initiateForm.end_date.isValid }">
                  <label for="end_date">End Date</label>
                  <input type="datetime-local" id="end_date" v-model.trim="initiateForm.end_date.val" placeholder=""
                    @blur="clearInitiateValidity('end_date')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.end_date.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.end_date.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col" :class="{ invalid: !initiateForm.entity_location.isValid }">
                  <label for="entity_location">Entity/Location*</label>
                  <input type="text" id="entity_location" v-model.trim="initiateForm.entity_location.val" placeholder=""
                    @blur="clearInitiateValidity('entity_location')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.entity_location.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.entity_location.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col" :class="{ invalid: !initiateForm.requester.isValid }">
                  <label for="requester">Requester*</label>
                  <input type="text" id="requester" v-model.trim="initiateForm.requester.val" placeholder=""
                    @blur="clearInitiateValidity('requester')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.requester.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.requester.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col" :class="{ invalid: !initiateForm.supervisor.isValid }">
                  <label for="supervisor">Supervisor*</label>
                  <input type="text" id="supervisor" v-model.trim="initiateForm.supervisor.val" placeholder=""
                    @blur="clearInitiateValidity('supervisor')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.supervisor.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.supervisor.msg }}
                    </p>
                  </transition>
                </div>
                <div class="flex flex-col sm:col-span-2" :class="{ invalid: !initiateForm.send_to_email.isValid }">
                  <label for="send_to_email">Send to Speaker / Representative (Email address)*</label>
                  <input type="email" id="send_to_email" v-model.trim="initiateForm.send_to_email.val" placeholder=""
                    @blur="clearInitiateValidity('send_to_email')" />
                  <transition name="fade" mode="out-in">
                    <p v-if="!initiateForm.send_to_email.isValid" class="h-4 text-xs text-red-600">
                      {{ initiateForm.send_to_email.msg }}
                    </p>
                  </transition>
                </div>
              </div>
              <div class="flex justify-end mt-6 mb-4">
                <button type="button" @click="closeInitiateModal"
                  class="border-2 font-medium border-gray-300 text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-50 rounded-lg px-5 py-2 uppercase text-sm cursor-pointer mr-3">
                  Cancel
                </button>
                <button type="submit" :disabled="isSubmittingInitiate"
                  class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
                  <span v-if="isSubmittingInitiate">Submitting...</span>
                  <span v-else>Submit</span>
                </button>
              </div>
            </form>
          </base-modal>
        </transition>
      </div>
    </div>
    <div class="h-full flex justify-center items-center" v-if="isLoading || isExportExcel">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import IconPlus from "../../components/icons/IconPlus.vue";
import SecurityClearanceList from "./components/SecurityClearanceList.vue";
import InitiatedRequests from "./components/InitiatedRequests.vue";
import xlsx from "xlsx/dist/xlsx.full.min.js";
export default {
  name: "security-clearance-index",
  components: {
    IconPlus,
    SecurityClearanceList,
    InitiatedRequests,
  },
  data() {
    return {
      activeTab: '',
      isLoading: false,
      isExportExcel: false,
      isApiCallRecurisive: false,
      generatedReport: [],
      showInitiateModal: false,
      showSuccessModal: false,
      isSubmittingInitiate: false,
      isLinkCopied: false,
      initiationToken: null,
      searchText: "",
      filterByEntity: "",
      initiateForm: {
        speaker: {
          val: "",
          isValid: true,
          msg: "",
        },
        event: {
          val: "",
          isValid: true,
          msg: "",
        },
        purpose_of_event: {
          val: "",
          isValid: true,
          msg: "",
        },
        target_group: {
          val: "",
          isValid: true,
          msg: "",
        },
        age_group: {
          val: "",
          isValid: true,
          msg: "",
        },
        date_time: {
          val: "",
          isValid: true,
          msg: "",
        },
        end_date: {
          val: "",
          isValid: true,
          msg: "",
        },
        entity_location: {
          val: "",
          isValid: true,
          msg: "",
        },
        requester: {
          val: "",
          isValid: true,
          msg: "",
        },
        supervisor: {
          val: "",
          isValid: true,
          msg: "",
        },
        send_to_email: {
          val: "",
          isValid: true,
          msg: "",
        },
      },
    };
  },
  created() {
    if (this.hasSuperPermission('security-clearance-index')) {
      this.activeTab = 'list';
    } else if (this.hasSuperPermission('security-clearance-initiate')) {
      this.activeTab = 'initiated';
    } else {
      this.activeTab = 'list';
    }

  },
  computed: {
    role() {
      return this.$store.getters.role;
    },
    initiationLink() {
      if (this.initiationToken) {
        return process.env.VUE_APP_FRONTEND_URL + 'security-clearance-form/' + this.initiationToken;
      }
      return '';
    },
  },
  methods: {
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    hasSuperPermission(crudName) {
      const permissions = this.$store.getters.permissions;
      const permissionsArray = permissions.split(",");
      if (permissionsArray.includes(crudName)) {
        return true;
      } else {
        return false;
      }
    },
    async handleApproveOrReject({ token, status, note }) {
      this.isLoading = true;

      const APPROVE_OR_REJECT_MUTATION = gql`
        mutation approveOrRejectSecurityClearance(
          $token: String!
          $status: Boolean!
          $note: String
        ) {
          approveOrRejectSecurityClearance(
            token: $token
            status: $status
            note: $note
          ) {
            error
            message
          }
        }
      `;

      let error_msg = null;
      let success_msg = null;

      try {
        await this.$apollo
          .mutate({
            mutation: APPROVE_OR_REJECT_MUTATION,
            variables: {
              token: token,
              status: status,
              note: note,
            },
          })
          .then((data) => {
            if (data.data.approveOrRejectSecurityClearance.error) {
              error_msg = data.data.approveOrRejectSecurityClearance.message;
            } else {
              success_msg = data.data.approveOrRejectSecurityClearance.message;
            }
          })
          .catch((error) => {
            console.error(error);
          });
      } catch (error) {
        error_msg = error.message || "Failed to authenticate, try later.";
      }

      if (error_msg === null) {
        this.showToast(success_msg);
        if (this.$refs.securityClearanceList) {
          this.$refs.securityClearanceList.refresh();
        }
      } else {
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              error_msg = null;
            }
          });
      }

      this.isLoading = false;
    },
    async handleGeneratePdf(payload) {
      if (payload && typeof payload === 'object' && payload.token) {
        this.generatePdf(payload.token, payload.fromInitiatedRequests);
      } else {
        this.generatePdf(payload, false);
      }
    },
    async handleDeleteSecurityClearance({ token, fullname }) {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete the security clearance application for ${fullname}. This action cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        this.isLoading = true;

        const DELETE_MUTATION = gql`
          mutation deleteSecurityClearance($token: String!) {
            deleteSecurityClearance(token: $token) {
              error
              message
            }
          }
        `;

        let error_msg = null;
        let success_msg = null;

        try {
          await this.$apollo
            .mutate({
              mutation: DELETE_MUTATION,
              variables: {
                token: token,
              },
            })
            .then((data) => {
              if (data.data.deleteSecurityClearance.error) {
                error_msg = data.data.deleteSecurityClearance.message;
              } else {
                success_msg = data.data.deleteSecurityClearance.message;
              }
            })
            .catch((error) => {
              console.error(error);
              error_msg = error.message || "Failed to delete security clearance.";
            });
        } catch (error) {
          error_msg = error.message || "Failed to delete security clearance, try later.";
        }

        if (error_msg === null) {
          this.showToast(success_msg);
          if (this.$refs.securityClearanceList) {
            this.$refs.securityClearanceList.refresh();
          }
        } else {
          this.$swal
            .fire({
              icon: "error",
              title: "Oops...",
              text: error_msg,
            })
            .then((result) => {
              if (result.value || result.dismiss) {
                error_msg = null;
              }
            });
        }

        this.isLoading = false;
      }
    },
    showToast(message = "") {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: message,
      });
    },
    async downloadAllFiles(token) {
      this.isLoading = true;

      const DOWNLOAD_FILES_MUTATION = gql`
        mutation DownloadAllSecurityClearanceFiles(
          $token: String
          $name: String!
        ) {
          downloadAllFiles(token: $token, name: $name) {
            error
            error_code
            message
            url
          }
        }
      `;

      let error_msg = null;
      let redirect_url = null;

      try {
        await this.$apollo
          .mutate({
            mutation: DOWNLOAD_FILES_MUTATION,
            variables: {
              token: token,
              name: "security-clearance",
            },
          })
          .then((data) => {
            if (data.data.downloadAllFiles.error) {
              error_msg = data.data.downloadAllFiles.message;
            } else {
              redirect_url = data.data.downloadAllFiles.url;
            }
          })
          .catch((error) => {
            error_msg = error;
          });
      } catch (error) {
        error_msg = error.message || "Failed to authenticate, try later.";
      }

      if (error_msg === null) {
        const url = process.env.VUE_APP_PUBLIC_URL + redirect_url;
        window.open(url, "_blank");
      } else {
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              error_msg = null;
            }
          });
      }

      this.isLoading = false;
    },
    async generatePdf(token, fromInitiatedRequests) {
      this.isLoading = true;

      const GENERATE_PDF_MUTATION = gql`
        mutation GeneratePdfSecurityClearance($token: String, $name: String!, $from_initiated_requests: Boolean) {
          generatePdf(token: $token, name: $name, from_initiated_requests: $from_initiated_requests) {
            error
            error_code
            message
            url
          }
        }
      `;

      let error_msg = null;
      let redirect_url = null;

      try {
        await this.$apollo
          .mutate({
            mutation: GENERATE_PDF_MUTATION,
            variables: {
              token: token,
              name: "security-clearance",
              from_initiated_requests: fromInitiatedRequests || null,
            },
          })
          .then((data) => {
            if (data.data.generatePdf.error) {
              error_msg = data.data.generatePdf.message;
            } else {
              redirect_url = data.data.generatePdf.url;
            }
          })
          .catch((error) => {
            error_msg = error;
          });
      } catch (error) {
        error_msg = error.message || "Failed to authenticate, try later.";
      }

      if (error_msg === null) {
        const url = process.env.VUE_APP_PUBLIC_URL + redirect_url;
        window.open(url, "_blank");
      } else {
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              error_msg = null;
            }
          });
      }

      this.isLoading = false;
    },
    async generateReport() {
      var page = 1;
      var limit = 200;
      this.isExportExcel = true;
      this.isApiCallRecurisive = true;
      while (this.isApiCallRecurisive) {
        const responseData = await this.fetchForExporting(page, limit);
        if (responseData.error) {
          this.$swal
            .fire({
              icon: "error",
              title: "Oops...",
              text: responseData.message,
            })
          break;
        }

        if (responseData.data.length > 0) {
          this.generatedReport.push(
            ...responseData.data.map((obj) => {
              return {
                fullname: obj.fullname,
                entity: obj.entity,
                application_type: obj.application_type,
                purpose_of_request: obj.purpose_of_request,
                date_of_birth: this.formatDate(obj.date_of_birth),
                place_of_birth: obj.place_of_birth,
                current_nationality: obj.current_nationality,
                previous_nationality: obj.previous_nationality,
                passport_number: obj.passport_number,
                place_of_issue: obj.place_of_issue,
                date_of_issue: this.formatDate(obj.date_of_issue),
                date_of_expiry: this.formatDate(obj.date_of_expiry),
                marital_status: obj.marital_status,
                religion: obj.religion,
                sect: obj.sect,
                mother_tongue: obj.mother_tongue,
                other_languages_known: obj.other_languages_known,
                father_name: obj.father_name,
                father_nationality: obj.father_nationality,
                father_profession: obj.father_profession,
                father_employer: obj.father_employer,
                father_date_of_birth: this.formatDate(obj.father_date_of_birth),
                father_place_of_birth: obj.father_place_of_birth,
                mother_name: obj.mother_name,
                mother_nationality: obj.mother_nationality,
                mother_profession: obj.mother_profession,
                mother_employer: obj.mother_employer,
                mother_date_of_birth: this.formatDate(obj.mother_date_of_birth),
                mother_place_of_birth: obj.mother_place_of_birth,
                highest_qualification: obj.highest_qualification,
                major: obj.major,
                institution: obj.institution,
                academic_qualification_country: obj.academic_qualification_country,
                academic_qualification_year: obj.academic_qualification_year,
                is_military_service: obj.is_military_service,
                military_service_country: obj.military_service_country,
                military_service_type_of_service: obj.military_service_type_of_service,
                military_service_rank: obj.military_service_rank,
                military_service_duration: obj.military_service_duration,
                initial_port_of_entry: obj.initial_port_of_entry,
                initial_date_of_entry: this.formatDate(obj.initial_date_of_entry),
                initial_employer: obj.initial_employer,
                initial_job_title: obj.initial_job_title,
                current_sponsor: obj.current_sponsor,
                current_residency_number: obj.current_residency_number,
                uid_number: obj.uid_number,
                sponsorship_details_place_of_issue: obj.sponsorship_details_place_of_issue,
                sponsorship_details_date_of_issue: this.formatDate(obj.sponsorship_details_date_of_issue),
                sponsorship_details_date_of_expiry: this.formatDate(obj.sponsorship_details_date_of_expiry),
                employment_status: obj.employment_status,
                current_employer: obj.current_employer,
                current_or_last_job_title: obj.current_or_last_job_title,
                employer_sector: obj.employer_sector,
                current_employment_city_or_country: obj.current_employment_city_or_country,
                res_info_uae_proprietor: obj.res_info_uae_proprietor,
                res_info_uae_city: obj.res_info_uae_city,
                res_info_uae_zone: obj.res_info_uae_zone,
                res_info_uae_street: obj.res_info_uae_street,
                res_info_uae_house: obj.res_info_uae_house,
                res_info_uae_floor_no: obj.res_info_uae_floor_no,
                res_info_uae_tel: obj.res_info_uae_tel,
                res_info_uae_po_box: obj.res_info_uae_po_box,
                res_info_home_country_proprietor: obj.res_info_home_country_proprietor,
                res_info_home_country_city: obj.res_info_home_country_city,
                res_info_home_country_zone: obj.res_info_home_country_zone,
                res_info_home_country_street: obj.res_info_home_country_street,
                res_info_home_country_house: obj.res_info_home_country_house,
                res_info_home_country_floor_no: obj.res_info_home_country_floor_no,
                res_info_home_country_tel: obj.res_info_home_country_tel,
                res_info_home_country_po_box: obj.res_info_home_country_po_box,
              };
            })
          );
          page++;
        } else {
          this.isApiCallRecurisive = false;
          break;
        }
      }
      this.exportToExcel("Security_Clearance");
    },
    async fetchForExporting(pageNo = 1, limitSize = 50) {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query ExportSecurityClearance(
              $page: Int
              $limit: Int
              $search: String
              $entity: String
            ) {
              exportSecurityClearance(
                page: $page
                limit: $limit
                search: $search
                entity: $entity
              ) {
                error
                error_code
                message
                data {
                  fullname
                  entity
                  application_type
                  purpose_of_request
                  date_of_birth
                  place_of_birth
                  current_nationality
                  previous_nationality
                  passport_number
                  place_of_issue
                  date_of_issue
                  date_of_expiry
                  marital_status
                  religion
                  sect
                  mother_tongue
                  other_languages_known
                  father_name
                  father_nationality
                  father_profession
                  father_employer
                  father_date_of_birth
                  father_place_of_birth
                  mother_name
                  mother_nationality
                  mother_profession
                  mother_employer
                  mother_date_of_birth
                  mother_place_of_birth
                  highest_qualification
                  major
                  institution
                  academic_qualification_country
                  academic_qualification_year
                  is_military_service
                  military_service_country
                  military_service_type_of_service
                  military_service_rank
                  military_service_duration
                  initial_port_of_entry
                  initial_date_of_entry
                  initial_employer
                  initial_job_title
                  current_sponsor
                  current_residency_number
                  uid_number
                  sponsorship_details_place_of_issue
                  sponsorship_details_date_of_issue
                  sponsorship_details_date_of_expiry
                  employment_status
                  current_employer
                  current_or_last_job_title
                  employer_sector
                  current_employment_city_or_country
                  res_info_uae_proprietor
                  res_info_uae_city
                  res_info_uae_zone
                  res_info_uae_street
                  res_info_uae_house
                  res_info_uae_floor_no
                  res_info_uae_tel
                  res_info_uae_po_box
                  res_info_home_country_proprietor
                  res_info_home_country_city
                  res_info_home_country_zone
                  res_info_home_country_street
                  res_info_home_country_house
                  res_info_home_country_floor_no
                  res_info_home_country_tel
                  res_info_home_country_po_box
                }
              }
            }
          `,
          fetchPolicy: "network-only",
          variables: {
            page: pageNo,
            limit: limitSize,
            search: this.searchText,
            entity: this.filterByEntity,
          },
        });

        const responseData = data.exportSecurityClearance;
        return responseData;
      } catch (error) {
        console.log(error.message);
      }
    },
    exportToExcel(reportName) {
      const XLSX = xlsx;

      let header = [
        "FULLNAME",
        "ENTITY",
        "APPLICATION TYPE",
        "PURPOSE OF REQUEST",
        "DATE OF BIRTH",
        "PLACE OF BIRTH",
        "CURRENT NATIONALITY",
        "PREVIOUS NATIONALITY",
        "PASSPORT NUMBER",
        "PLACE OF ISSUE",
        "DATE OF ISSUE",
        "DATE OF EXPIRY",
        "MARITAL STATUS",
        "RELIGION",
        "SECT",
        "MOTHER TONGUE",
        "OTHER LANGUAGES KNOWN",
        "FATHER'S NAME",
        "FATHER'S NATIONALITY",
        "FATHER'S PROFESSION",
        "FATHER'S EMPLOYER",
        "FATHER'S DATE OF BIRTH",
        "FATHER'S PLACE OF BIRTH",
        "MOTHER'S NAME",
        "MOTHER'S NATIONALITY",
        "MOTHER'S PROFESSION",
        "MOTHER'S EMPLOYER",
        "MOTHER'S DATE OF BIRTH",
        "MOTHER'S PLACE OF BIRTH",
        "HIGHEST QUALIFICATION",
        "MAJOR",
        "INSTITUTION",
        "ACADEMIC QUALIFICATION COUNTRY",
        "ACADEMIC QUALIFICATION YEAR",
        "HAVE YOU EVER BEEN IN THE MILITARY SERVICES",
        "MILITARY SERVICE COUNTRY",
        "MILITARY SERVICE TYPE OF SERVICE",
        "MILITARY SERVICE RANK",
        "MILITARY SERVICE DURATION",
        "INITIAL PORT OF ENTRY",
        "INITIAL DATE OF ENTRY",
        "INITIAL EMPLOYER",
        "INITIAL JOB TITLE",
        "CURRENT SPONSOR",
        "CURRENT RESIDENCY NUMBER",
        "UID NUMBER",
        "SPONSORSHIP DETAILS PLACE OF ISSUE",
        "SPONSORSHIP DETAILS DATE OF ISSUE",
        "SPONSORSHIP DETAILS DATE OF EXPIRY",
        "EMPLOYMENT STATUS",
        "CURRENT EMPLOYER",
        "CURRENT OR LAST JOB TITLE",
        "EMPLOYER SECTOR",
        "CURRENT EMPLOYMENT CITY OR COUNTRY",
        "RESIDENCE INFO UAE PROPRIETOR",
        "RESIDENCE INFO UAE CITY",
        "RESIDENCE INFO UAE ZONE",
        "RESIDENCE INFO UAE STREET",
        "RESIDENCE INFO UAE HOUSE",
        "RESIDENCE INFO UAE FLOOR NO",
        "RESIDENCE INFO UAE TEL",
        "RESIDENCE INFO UAE PO BOX",
        "RESIDENCE INFO HOME COUNTRY PROPRIETOR",
        "RESIDENCE INFO HOME COUNTRY CITY",
        "RESIDENCE INFO HOME COUNTRY ZONE",
        "RESIDENCE INFO HOME COUNTRY STREET",
        "RESIDENCE INFO HOME COUNTRY HOUSE",
        "RESIDENCE INFO HOME COUNTRY FLOOR NO",
        "RESIDENCE INFO HOME COUNTRY TEL",
        "RESIDENCE INFO HOME COUNTRY PO BOX",
      ];

      const worksheet = XLSX.utils.json_to_sheet(this.generatedReport);
      const columnWidths = [];

      // Dynamically set headers in the worksheet
      for (let i = 0; i < header.length; i++) {
        const column = String.fromCharCode(65 + (i % 26)); // Generate column letters (A-Z)
        const row = Math.floor(i / 26); // Determine if the column extends beyond Z (AA, AB, etc.)
        const columnLetter = row > 0 ? String.fromCharCode(64 + row) + column : column; // Handle multi-letter columns
        worksheet[`${columnLetter}1`] = { v: header[i], t: "s" }; // Assign header value

        // Set a default width dynamically based on the header text length
        columnWidths.push({ width: Math.max(40, header[i].length + 5) }); // Ensure a minimum width of 40
      }

      // Assign dynamic column widths to the worksheet
      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `${reportName}.xlsx`);

      this.generatedReport = [];
      this.isExportExcel = false;
      this.isApiCallRecurisive = false;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    },
    async deleteSecurityClearance(index) {
      const item = this.items[index];

      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete the security clearance application for ${item.fullname}. This action cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        this.isLoading = true;

        const DELETE_MUTATION = gql`
          mutation deleteSecurityClearance($token: String!) {
            deleteSecurityClearance(token: $token) {
              error
              message
            }
          }
        `;

        let error_msg = null;
        let success_msg = null;

        try {
          await this.$apollo
            .mutate({
              mutation: DELETE_MUTATION,
              variables: {
                token: item.token,
              },
            })
            .then((data) => {
              if (data.data.deleteSecurityClearance.error) {
                error_msg = data.data.deleteSecurityClearance.message;
              } else {
                success_msg = data.data.deleteSecurityClearance.message;
              }
            })
            .catch((error) => {
              console.error(error);
              error_msg = error.message || "Failed to delete security clearance.";
            });
        } catch (error) {
          error_msg = error.message || "Failed to delete security clearance, try later.";
        }

        if (error_msg === null) {
          this.showToast(success_msg);
        } else {
          this.$swal
            .fire({
              icon: "error",
              title: "Oops...",
              text: error_msg,
            })
            .then((result) => {
              if (result.value || result.dismiss) {
                error_msg = null;
              }
            });
        }

        this.isLoading = false;
      }
    },
    openInitiateModal() {
      this.showInitiateModal = true;
    },
    closeInitiateModal() {
      this.showInitiateModal = false;
      this.resetInitiateForm();
    },
    resetInitiateForm() {
      this.initiateForm = {
        speaker: {
          val: "",
          isValid: true,
          msg: "",
        },
        event: {
          val: "",
          isValid: true,
          msg: "",
        },
        purpose_of_event: {
          val: "",
          isValid: true,
          msg: "",
        },
        target_group: {
          val: "",
          isValid: true,
          msg: "",
        },
        age_group: {
          val: "",
          isValid: true,
          msg: "",
        },
        date_time: {
          val: "",
          isValid: true,
          msg: "",
        },
        entity_location: {
          val: "",
          isValid: true,
          msg: "",
        },
        requester: {
          val: "",
          isValid: true,
          msg: "",
        },
        supervisor: {
          val: "",
          isValid: true,
          msg: "",
        },
        send_to_email: {
          val: "",
          isValid: true,
          msg: "",
        },
      };
    },
    clearInitiateValidity(input) {
      this.initiateForm[input].isValid = true;
      this.initiateForm[input].msg = "";
    },
    validateInitiateForm() {
      let isValid = true;

      if (this.initiateForm.speaker.val === "") {
        this.initiateForm.speaker.isValid = false;
        this.initiateForm.speaker.msg = "Speaker / Representative field is required.";
        isValid = false;
      }

      if (this.initiateForm.event.val === "") {
        this.initiateForm.event.isValid = false;
        this.initiateForm.event.msg = "Event field is required.";
        isValid = false;
      }

      if (this.initiateForm.purpose_of_event.val === "") {
        this.initiateForm.purpose_of_event.isValid = false;
        this.initiateForm.purpose_of_event.msg = "Purpose of Event field is required.";
        isValid = false;
      }

      if (this.initiateForm.target_group.val === "") {
        this.initiateForm.target_group.isValid = false;
        this.initiateForm.target_group.msg = "Target Group field is required.";
        isValid = false;
      }

      if (this.initiateForm.age_group.val === "") {
        this.initiateForm.age_group.isValid = false;
        this.initiateForm.age_group.msg = "Age Group field is required.";
        isValid = false;
      }

      if (this.initiateForm.date_time.val === "") {
        this.initiateForm.date_time.isValid = false;
        this.initiateForm.date_time.msg = "Start Date field is required.";
        isValid = false;
      }

      if (this.initiateForm.end_date.val === "") {
        this.initiateForm.end_date.isValid = false;
        this.initiateForm.end_date.msg = "End Date field is required.";
        isValid = false;
      }

      if (this.initiateForm.entity_location.val === "") {
        this.initiateForm.entity_location.isValid = false;
        this.initiateForm.entity_location.msg = "Entity/Location field is required.";
        isValid = false;
      }

      if (this.initiateForm.requester.val === "") {
        this.initiateForm.requester.isValid = false;
        this.initiateForm.requester.msg = "Requester field is required.";
        isValid = false;
      }

      if (this.initiateForm.supervisor.val === "") {
        this.initiateForm.supervisor.isValid = false;
        this.initiateForm.supervisor.msg = "Supervisor field is required.";
        isValid = false;
      }

      if (this.initiateForm.send_to_email.val === "") {
        this.initiateForm.send_to_email.isValid = false;
        this.initiateForm.send_to_email.msg = "Send to Speaker / Representative Email field is required.";
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.initiateForm.send_to_email.val)) {
          this.initiateForm.send_to_email.isValid = false;
          this.initiateForm.send_to_email.msg = "Please enter a valid email address.";
          isValid = false;
        }
      }

      return isValid;
    },
    async submitInitiateForm() {
      if (!this.validateInitiateForm()) {
        return;
      }

      this.isSubmittingInitiate = true;

      const INITIATE_MUTATION = gql`
        mutation InitiateSecurityClearance($input: SecurityClearanceInitiationInput!) {
          initiateSecurityClearance(input: $input) {
            error
            error_code
            message
            token
            initiation {
              id
              token
              speaker
              event
            }
          }
        }
      `;

      let error_msg = null;
      let success_msg = null;
      let token = null;

      try {
        await this.$apollo
          .mutate({
            mutation: INITIATE_MUTATION,
            variables: {
              input: {
                speaker: this.initiateForm.speaker.val,
                event: this.initiateForm.event.val,
                purpose_of_event: this.initiateForm.purpose_of_event.val,
                target_group: this.initiateForm.target_group.val,
                age_group: this.initiateForm.age_group.val,
                date_time: this.formatDateTimeForGraphQL(this.initiateForm.date_time.val),
                end_date: this.initiateForm.end_date.val ? this.formatDateTimeForGraphQL(this.initiateForm.end_date.val) : null,
                entity_location: this.initiateForm.entity_location.val,
                requester: this.initiateForm.requester.val,
                supervisor: this.initiateForm.supervisor.val,
                send_to_email: this.initiateForm.send_to_email.val,
              },
            },
          })
          .then((data) => {
            if (data.data.initiateSecurityClearance.error) {
              error_msg = data.data.initiateSecurityClearance.message;
            } else {
              success_msg = data.data.initiateSecurityClearance.message;
              token = data.data.initiateSecurityClearance.token;
            }
          })
          .catch((error) => {
            error_msg = error.message || "Failed to initiate security clearance.";
          });
      } catch (error) {
        error_msg = error.message || "Failed to initiate security clearance, try later.";
      }

      this.isSubmittingInitiate = false;

      if (error_msg === null) {
        this.initiationToken = token;
        this.closeInitiateModal();
        this.showToast(success_msg);
        if (this.$refs.initiatedRequests) {
          this.$refs.initiatedRequests.refresh();
        }
        setTimeout(() => {
          this.showSuccessModal = true;
        }, 500);
      } else {
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              error_msg = null;
            }
          });
      }
    },
    formatDateTimeForGraphQL(dateTimeLocal) {
      if (!dateTimeLocal) return "";
      const date = new Date(dateTimeLocal);
      const pad = function (num) {
        return String(num).padStart(2, '0');
      };
      const y = date.getFullYear();
      const m = pad(date.getMonth() + 1);
      const d = pad(date.getDate());
      const h = pad(date.getHours());
      const i = pad(date.getMinutes());
      const s = pad(date.getSeconds());
      return `${y}-${m}-${d} ${h}:${i}:${s}`;
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.initiationToken = null;
      this.isLinkCopied = false;
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.initiationLink);
        this.isLinkCopied = true;
        setTimeout(() => {
          this.isLinkCopied = false;
        }, 2000);
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = this.initiationLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          this.isLinkCopied = true;
          setTimeout(() => {
            this.isLinkCopied = false;
          }, 2000);
        } catch (err) {
          console.error('Failed to copy link:', err);
        }
        document.body.removeChild(textArea);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.5s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
  opacity: 0;
  height: 0px;
}

label {
  @apply mb-1 block text-sm;
}

input[type="text"],
input[type="email"],
input[type="datetime-local"],
textarea {
  @apply block w-full border rounded-lg py-2 px-2;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="datetime-local"]:focus,
textarea:focus {
  outline: none;
  border-color: #394867;
  box-shadow: 0 0 0 2px #394867;
}

.invalid label {
  @apply text-red-600;
}

.invalid input,
.invalid textarea {
  border: 1px solid red;
}
</style>
